import { Badge } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import type { TagSelection } from '@/entities/brand-media';

/**
 * Which tags are narrowing the column, beside the folder that is narrowing it too.
 *
 * The rail lights the rows it has chosen, but it can be hidden, its groups fold, and it scrolls --
 * so a filter can be running with nothing on screen saying so, and a short grid then reads as a
 * folder with almost nothing in it. These sit against the title that filter applies to, which is
 * where someone asking why they are seeing so little is already looking.
 *
 * Each carries its own removal rather than sending a person back to the rail to find the row they
 * chose from: undoing a filter should cost the same as reading it.
 */
export const TagFilterChips = ({
  selection,
  onRemove,
}: {
  selection: TagSelection;
  onRemove: (key: string, value: string) => void;
}) => {
  const { t } = useTranslation('tools');

  const pairs = Object.entries(selection).flatMap(([key, values]) =>
    values.map((value) => ({ key, value })),
  );

  if (pairs.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {pairs.map(({ key, value }) => (
        // The key travels with the value for the reason the rail groups by it: a value alone cannot
        // say which question it answers, and two facets can hold the same word.
        <Badge
          key={`${key}:${value}`}
          variant="secondary"
          roundness="full"
          onRemove={() => onRemove(key, value)}
          removeLabel={t('brandMedia.clearTagFilter', { key, value })}
        >
          {t('brandMedia.tagFilter', { key, value })}
        </Badge>
      ))}
    </div>
  );
};
